import { useState, type KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  variant?: "default" | "include" | "exclude";
  ariaLabel: string;
}

export function ChipsInput({ value, onChange, placeholder, variant = "default", ariaLabel }: Props) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = draft.trim();
    if (!v || value.includes(v)) return;
    onChange([...value, v]);
    setDraft("");
  };

  const remove = (term: string) => {
    onChange(value.filter((t) => t !== term));
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add();
    }
  };

  const badgeClass =
    variant === "exclude"
      ? "bg-red-50 text-red-900 border-red-200"
      : variant === "include"
        ? "bg-green-50 text-green-900 border-green-200"
        : "";

  return (
    <div aria-label={ariaLabel}>
      <div className="flex flex-wrap gap-1 mb-2">
        {value.map((term) => (
          <Badge key={term} variant="outline" className={badgeClass}>
            {term}
            <Button
              variant="ghost"
              size="sm"
              className="ms-1 h-4 w-4 p-0"
              onClick={() => remove(term)}
              aria-label={`Remove ${term}`}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder={placeholder ?? "Add and press Enter"}
        />
        <Button type="button" variant="outline" onClick={add}>
          Add
        </Button>
      </div>
    </div>
  );
}
